/**
 * №53 (53.2) — THE JOB DESCRIPTION, DERIVED.
 *
 * Drawn in `docs/53-mockups/workforce-2.png`, whose caption is the whole spec for
 * this file:
 *
 * > **"Will never do" is derived, not typed.** Each line reads out of a wall the
 * > system already enforces — a capability area it was not granted, the money
 * > wall, the ledger's authorisation path, the autonomy setting — so it can never
 * > claim more restraint than the engine actually applies. **A hand-kept list that
 * > disagrees with the policy engine is worse than no list at all.**
 *
 * That is why this module is pure and why every line it returns carries the WALL
 * that produced it. A restraint without a named enforcer is a promise; a restraint
 * that names `billing.post` is a fact a reader can go and check.
 *
 * The left column is the opposite and is honest about it: **"Will do, on its own"
 * is DECLARED** (`AgentConfig.will_do`). It describes intent, which no wall can
 * derive — the engine knows what a hire may not touch, not what it is for. So it
 * is authored, and it renders only what was authored (D-CR2): a hire with nothing
 * declared shows an empty column saying so, never a plausible invention.
 */
import type { AreaDecl } from "../../hooks/use-policy";

/** One restraint, and the wall that enforces it. Both halves are required. */
export interface Restraint {
  /** What it will not do, in a principal's words. */
  text: string;
  /** The wall, in the engine's words — `money wall · billing.post`. */
  wall: string;
}

export interface JobDescriptionInput {
  /** Every declared area, granted or not. */
  areas: AreaDecl[];
  /** The area keys this hire actually holds (roles ∪ default_member). */
  granted: Set<string>;
  /** `off` | `supervised` | `autonomous`; absent reads as the safe default. */
  autonomy?: string | null;
}

/**
 * The right-hand column. Ordered most-consequential first, which is also roughly
 * the order a principal worries about them.
 */
export function deriveWillNeverDo(input: JobDescriptionInput): Restraint[] {
  const { areas, granted } = input;
  const out: Restraint[] = [];

  // ① Areas the hire was NOT granted. These are the engine's own words for its
  //    own refusals — the area's declared label, and the key that refuses.
  for (const a of areas) {
    if (granted.has(a.key)) continue;
    if (a.money) continue; // the money wall says it better, once, below
    out.push({ text: a.label, wall: `area · ${a.key} — not granted` });
  }

  // ② The card wall. Not per-hire and not conditional: in Kuvio an outbound that
  //    changes a term IS a card, for every hire, which is why it is stated for
  //    every hire rather than derived from one's configuration.
  out.push({
    text: "Agree a date, a price or a term with the other side",
    wall: "every outbound that changes terms is a card",
  });

  // ③ The money wall — the withheld money areas, named by key. `deriveCapabilities`
  //    computes the same set for the desk; this reads it out for a reader.
  const withheldMoney = areas.filter((a) => a.money && !granted.has(a.key));
  if (withheldMoney.length > 0) {
    out.push({
      text: "Post an invoice or write off time",
      wall: `money wall · ${withheldMoney.map((a) => a.key).join(" · ")}`,
    });
  }

  // ④ The ledger's authorisation path. A hire that HOLDS `trust.operate` is the
  //    interesting case, not the exempt one: it may draft, and every draft still
  //    lands with a signatory, because a machine member is never one
  //    (`autonomous_cannot_approve`).
  if (granted.has("trust.operate")) {
    out.push({
      text: "Touch the trust ledger without a signatory",
      wall: "ledger authorisation · posts under your name",
    });
  } else {
    out.push({ text: "Touch the trust ledger", wall: "area · trust.operate — not granted" });
  }

  // ⑤ Autonomy. D-CL4: the dial governs WHEN a hire runs, never what it may
  //    touch — so this line is about being unattended, and says only that.
  const autonomy = input.autonomy ?? "off";
  if (autonomy !== "autonomous") {
    out.push({
      text: "Act while you are not watching, until you say so",
      wall: `autonomy · ${autonomy}`,
    });
  }

  return out;
}

/**
 * The left column, read from the declared field. Never derived, never inferred
 * from equipment — "what it may touch" and "what it is for" are different
 * questions and only the first has a wall behind it.
 */
export function willDoLines(declared: readonly string[] | null | undefined): string[] {
  return (declared ?? []).map((l) => l.trim()).filter((l) => l.length > 0);
}
